import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  LayoutGrid,
  Table2,
  ChevronRight,
  DollarSign,
  Building2,
  Calendar,
  FileText,
  Upload,
  Download,
  User,
} from 'lucide-react';
import {
  useOpportunities,
  usePipelineSummary,
  useCreateOpportunity,
  Opportunity,
  ProjectSummary,
  ScopingStage,
  SCOPING_STAGES,
} from '@/hooks/useOpportunities';
import { useHasPermission } from '@/hooks/useAuth';
import { useUpdateProjectScopingStage } from '@/hooks/useProjects';
import { useBulkImportOpportunities, getOpportunitiesExportUrl, csvToOpportunities } from '@/hooks/useBulkOperations';
import { useRateCardTypes } from '@/hooks/useRateCards';
import { useUserLookup } from '@/hooks/useUserLookup';
import { CustomerSelect } from '@/components/CustomerSelect';
import { ExpandableOpportunityRow } from '@/components/pipeline/ExpandableOpportunityRow';
import { AddProjectToOpportunity } from '@/components/pipeline/AddProjectToOpportunity';
import { BulkImportModal } from '@/components/common/BulkImportModal';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { MobileFilterDrawer, MobileFilterButton, FilterField } from '@/components/common/MobileFilterDrawer';
import { cn } from '@/lib/utils';

const OPPORTUNITY_IMPORT_COLUMNS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'customerId', label: 'Customer ID', required: true },
  { key: 'salesOrg', label: 'Sales Org' },
  { key: 'opportunityOwner', label: 'Owner' },
  { key: 'assignedArchitect', label: 'Architect' },
  { key: 'probability', label: 'Probability (%)' },
  { key: 'forecastCloseDate', label: 'Forecast Close Date' },
  { key: 'notes', label: 'Notes' },
];

type ViewMode = 'table' | 'kanban';

export function Opportunities() {
  const navigate = useNavigate();
  const canCreate = useHasPermission('pipeline.opportunities.create');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<ScopingStage | ''>('');
  const [fundingFilter, setFundingFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [addProjectOpportunity, setAddProjectOpportunity] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const [adFilter, setAdFilter] = useState<string>('');
  const bulkImport = useBulkImportOpportunities();
  const { data: typesData } = useRateCardTypes();
  const { data: usersData } = useUserLookup();

  const activeFilterCount = [stageFilter, fundingFilter, adFilter].filter(Boolean).length;

  const resetFilters = () => {
    setStageFilter('');
    setFundingFilter('');
    setAdFilter('');
  };

  const { data: opportunitiesData, isLoading, refetch } = useOpportunities({
    search: searchQuery || undefined,
    stage: stageFilter || undefined,
    accountDirectorId: adFilter || undefined,
    status: 'pipeline',
    limit: 200,
  });

  const { data: summaryData } = usePipelineSummary();

  // Apply client-side funding filter
  const opportunities = useMemo(() => {
    const opps = opportunitiesData?.opportunities || [];
    if (!fundingFilter) return opps;
    return opps.filter((o) => o.fundingTypeId === fundingFilter);
  }, [opportunitiesData?.opportunities, fundingFilter]);

  // Collect all projects from opportunities for Kanban view
  const allProjects = useMemo(() => {
    const projects: Array<ProjectSummary & { opportunityTitle: string; customerName: string }> = [];
    for (const opp of opportunities) {
      if (opp.projects) {
        for (const project of opp.projects) {
          projects.push({
            ...project,
            opportunityTitle: opp.title,
            customerName: opp.customerName,
          });
        }
      }
    }
    return projects;
  }, [opportunities]);

  // Group projects by stage for Kanban
  const projectsByStage = useMemo(() => {
    const grouped: Record<ScopingStage, Array<ProjectSummary & { opportunityTitle: string; customerName: string }>> = {
      partner_briefing: [],
      services_positioned: [],
      scoping_workshop: [],
      customer_review: [],
      customer_approval: [],
      po_sent: [],
      in_delivery: [],
    };

    for (const project of allProjects) {
      const stage: ScopingStage = (project.currentScopingStage || 'partner_briefing') as ScopingStage;
      if (grouped[stage]) {
        grouped[stage].push(project);
      }
    }

    return grouped;
  }, [allProjects]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-xtravirt-dark">Pipeline</h1>
          <p className="mt-1 text-sm text-gray-600">
            {opportunities.length} opportunities
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={getOpportunitiesExportUrl()}
            className="btn-secondary hidden sm:flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            <span className="hidden md:inline">Export</span>
          </a>
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary hidden sm:flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            <span className="hidden md:inline">Import</span>
          </button>
          {canCreate && (
            <button onClick={() => setShowCreateModal(true)} className="btn-primary">
              <Plus className="mr-1 md:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">New Opportunity</span>
              <span className="sm:hidden">New</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-3 pb-4">
        {/* Search - always visible */}
        <div className="relative flex-1 min-w-[150px] md:max-w-[300px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-9 w-full"
          />
        </div>

        {/* Mobile filter button */}
        <MobileFilterButton
          onClick={() => setShowFilterDrawer(true)}
          hasActiveFilters={activeFilterCount > 0}
          activeCount={activeFilterCount}
        />

        {/* Desktop filters */}
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as ScopingStage | '')}
          className="input w-44 hidden md:block"
        >
          <option value="">All Stages</option>
          {SCOPING_STAGES.map((stage) => (
            <option key={stage.id} value={stage.id}>
              {stage.name}
            </option>
          ))}
        </select>

        <select
          value={fundingFilter}
          onChange={(e) => setFundingFilter(e.target.value)}
          className="input w-36 hidden md:block"
        >
          <option value="">All Funding</option>
          {typesData?.types?.map(type => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>

        <select
          value={adFilter}
          onChange={(e) => setAdFilter(e.target.value)}
          className="input w-44 hidden md:block"
        >
          <option value="">All Account Directors</option>
          {usersData?.users?.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>

        <div className="flex-1 hidden md:block" />

        {/* View toggle - hidden on mobile (default to card view) */}
        <div className="hidden md:flex rounded-lg border border-gray-200 p-1 bg-white">
          <button
            onClick={() => setViewMode('table')}
            className={cn(
              'rounded-md p-1.5 transition-colors',
              viewMode === 'table'
                ? 'bg-xtravirt-cyan text-white'
                : 'text-gray-500 hover:text-gray-700'
            )}
            title="Table view"
          >
            <Table2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={cn(
              'rounded-md p-1.5 transition-colors',
              viewMode === 'kanban'
                ? 'bg-xtravirt-cyan text-white'
                : 'text-gray-500 hover:text-gray-700'
            )}
            title="Kanban view"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="summary-bar mb-4">
        <div className="summary-stat">
          <p className="summary-stat-label">Opportunities</p>
          <p className="summary-stat-value">{summaryData?.pipeline.totalCount || 0}</p>
          <p className="text-[10px] opacity-70 mt-0.5">{allProjects.length} projects</p>
        </div>
        <div className="summary-stat">
          <p className="summary-stat-label">Total Value</p>
          <p className="summary-stat-value">{formatCurrency(summaryData?.pipeline.totalValue || 0)}</p>
        </div>
        <div className="summary-stat hidden sm:block">
          <p className="summary-stat-label">Scoped</p>
          <p className="summary-stat-value success">{formatCurrency(summaryData?.pipeline.totalScopedValue || 0)}</p>
        </div>
        <div className="summary-stat hidden sm:block">
          <p className="summary-stat-label">Unscoped</p>
          <p className="summary-stat-value highlight">{formatCurrency(summaryData?.pipeline.totalUnscopedValue || 0)}</p>
        </div>
        <div className="summary-stat hidden lg:block">
          <p className="summary-stat-label">In Delivery</p>
          <p className="summary-stat-value">{summaryData?.inDelivery.count || 0}</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {/* Mobile: Always show card view */}
        <div className="md:hidden space-y-3 overflow-y-auto pb-4">
          {opportunities.length > 0 ? (
            opportunities.map((opp) => (
              <OpportunityMobileCard
                key={opp.id}
                opportunity={opp}
                onClick={() => navigate(`/opportunities/${opp.id}`)}
              />
            ))
          ) : (
            <div className="px-4 py-12 text-center text-gray-500 bg-white rounded-lg border-2 border-dashed">
              No opportunities found
            </div>
          )}
        </div>

        {/* Desktop: Table or Kanban based on toggle */}
        <div className="hidden md:block h-full">
          {viewMode === 'table' ? (
            <div className="card overflow-auto !p-0">
              {/* Table Header */}
              <div className="grid grid-cols-[auto_1fr_140px_120px_110px_110px_90px] items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium uppercase tracking-wider text-gray-500">
                <div className="w-4" />
                <div>Customer / Opportunity</div>
                <div>Account Director</div>
                <div>Funding</div>
                <div className="text-right">Scoped</div>
                <div className="text-right">Unscoped</div>
                <div className="text-center">Projects</div>
              </div>
              {/* Expandable Rows */}
              {opportunities.length > 0 ? (
                opportunities.map((opp) => (
                  <ExpandableOpportunityRow
                    key={opp.id}
                    opportunity={opp}
                    onAddProject={(id) =>
                      setAddProjectOpportunity({ id, title: opp.title })
                    }
                  />
                ))
              ) : (
                <div className="px-4 py-12 text-center text-gray-500">
                  No opportunities found
                </div>
              )}
            </div>
          ) : (
            <ProjectKanbanBoard
              projectsByStage={projectsByStage}
              onCardClick={(id) => navigate(`/projects/${id}`)}
              onRefresh={refetch}
            />
          )}
        </div>
      </div>

      {/* Create Opportunity Modal */}
      {showCreateModal && (
        <CreateOpportunityModal onClose={() => setShowCreateModal(false)} />
      )}

      {/* Add Project Modal */}
      {addProjectOpportunity && (
        <AddProjectToOpportunity
          opportunityId={addProjectOpportunity.id}
          opportunityTitle={addProjectOpportunity.title}
          onClose={() => setAddProjectOpportunity(null)}
          onSuccess={() => refetch()}
        />
      )}

      {/* Import Modal */}
      <BulkImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Opportunities"
        columns={OPPORTUNITY_IMPORT_COLUMNS}
        onImport={async (data) => {
          return bulkImport.mutateAsync(data as unknown as Parameters<typeof bulkImport.mutateAsync>[0]);
        }}
        transformRow={(row, mapping) => {
          const opportunities = csvToOpportunities([row], mapping);
          return (opportunities[0] || null) as unknown as Record<string, unknown>;
        }}
      />

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onReset={resetFilters}
        hasActiveFilters={activeFilterCount > 0}
      >
        <FilterField label="Stage">
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as ScopingStage | '')}
            className="input w-full"
          >
            <option value="">All Stages</option>
            {SCOPING_STAGES.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Funding">
          <select
            value={fundingFilter}
            onChange={(e) => setFundingFilter(e.target.value)}
            className="input w-full"
          >
            <option value="">All Funding</option>
            {typesData?.types?.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Account Director">
          <select
            value={adFilter}
            onChange={(e) => setAdFilter(e.target.value)}
            className="input w-full"
          >
            <option value="">All Account Directors</option>
            {usersData?.users?.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </FilterField>
      </MobileFilterDrawer>
    </div>
  );
}

// ============ Opportunity Mobile Card ============

function OpportunityMobileCard({ opportunity, onClick }: { opportunity: Opportunity; onClick: () => void }) {
  const projects = opportunity.projects || [];
  const scopedValue = opportunity.scopedValue || opportunity.totalValue || 0;
  const unscopedValue = opportunity.unscopedValue || 0;
  const servicesValue = opportunity.servicesValue || 0;

  return (
    <div
      onClick={onClick}
      className="mobile-card cursor-pointer"
    >
      {/* Title & Funding */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-xtravirt-dark text-sm truncate">
            {opportunity.title}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
            <Building2 className="h-3 w-3" />
            {opportunity.customerName}
          </p>
        </div>
        {(opportunity.fundingTypeName) && (
          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-700 flex-shrink-0">
            {opportunity.fundingTypeName}
          </span>
        )}
      </div>

      {/* Account Director */}
      {opportunity.accountDirectorName && (
        <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-500">
          <User className="h-3 w-3" />
          <span>{opportunity.accountDirectorName}</span>
        </div>
      )}

      {/* Opportunity Value Summary */}
      <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">Opportunity Value</span>
          <span className="font-semibold text-xtravirt-dark">
            {servicesValue > 0 ? formatCurrency(servicesValue, opportunity.currency) : '—'}
          </span>
        </div>
        {scopedValue > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Scoped</span>
            <span className="font-medium text-green-700">
              {formatCurrency(scopedValue, opportunity.currency)}
            </span>
          </div>
        )}
        {unscopedValue > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Unscoped</span>
            <span className="font-medium text-amber-600">
              {formatCurrency(unscopedValue, opportunity.currency)}
            </span>
          </div>
        )}
      </div>

      {/* Projects as line items */}
      {projects.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Projects</span>
          {projects.map((project) => {
            const stageInfo = SCOPING_STAGES.find((s) => s.id === project.currentScopingStage);
            return (
              <div key={project.id} className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-700 truncate flex-1 min-w-0">
                  {project.name}
                </span>
                {stageInfo && (
                  <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0', stageInfo.color)}>
                    {stageInfo.name}
                  </span>
                )}
                <span className="text-xs font-medium flex-shrink-0">
                  {project.totalCost > 0
                    ? <span className="text-green-700">{formatCurrency(project.totalCost, opportunity.currency)}</span>
                    : <span className="text-gray-400">—</span>
                  }
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ Project Kanban Board ============

type ProjectWithContext = ProjectSummary & { opportunityTitle: string; customerName: string };

interface ProjectKanbanBoardProps {
  projectsByStage: Record<ScopingStage, ProjectWithContext[]>;
  onCardClick: (id: string) => void;
  onRefresh: () => void;
}

function ProjectKanbanBoard({ projectsByStage, onCardClick, onRefresh }: ProjectKanbanBoardProps) {
  const updateStage = useUpdateProjectScopingStage();

  const handleDragStart = (e: React.DragEvent, projectId: string) => {
    e.dataTransfer.setData('projectId', projectId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetStage: ScopingStage) => {
    e.preventDefault();
    const projectId = e.dataTransfer.getData('projectId');
    if (projectId) {
      try {
        await updateStage.mutateAsync({
          projectId,
          stageId: targetStage,
          status: 'in_progress',
        });
        onRefresh();
      } catch (error) {
        console.error('Failed to update project stage:', error);
      }
    }
  };

  // Calculate stage totals from projects
  const getStageValue = (stageId: ScopingStage) => {
    return projectsByStage[stageId].reduce((sum, p) => sum + (p.totalCost || 0), 0);
  };

  return (
    <div className="kanban-board flex gap-3 h-full overflow-x-auto pb-4">
      {SCOPING_STAGES.map((stage) => {
        const stageProjects = projectsByStage[stage.id];
        const stageValue = getStageValue(stage.id);

        return (
          <div
            key={stage.id}
            className="kanban-column"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.id)}
          >
            {/* Column Header */}
            <div className={cn('kanban-column-header', stage.color)}>
              <div className="flex items-center justify-between">
                <h3 className="kanban-column-title">{stage.name}</h3>
                <span className="kanban-column-count">
                  {stageProjects.length}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-0.5">
                {formatCurrency(stageValue)}
              </p>
            </div>

            {/* Cards */}
            <div className="kanban-column-content">
              {stageProjects.map((project) => (
                <ProjectKanbanCard
                  key={project.id}
                  project={project}
                  onClick={() => onCardClick(project.id)}
                  onDragStart={(e) => handleDragStart(e, project.id)}
                />
              ))}

              {stageProjects.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-400">
                  No projects
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============ Project Kanban Card ============

interface ProjectKanbanCardProps {
  project: ProjectWithContext;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
}

function ProjectKanbanCard({ project, onClick, onDragStart }: ProjectKanbanCardProps) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="kanban-card"
    >
      <div className="kanban-card-header">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-xtravirt-cyan flex-shrink-0" />
          <h4 className="kanban-card-title">
            {project.name}
          </h4>
        </div>
        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
      </div>

      <div className="kanban-card-meta">
        <Building2 className="h-3 w-3" />
        <span className="truncate">{project.customerName}</span>
      </div>

      <div className="kanban-card-meta mt-1">
        <span className="text-xs text-gray-400 truncate">{project.opportunityTitle}</span>
      </div>

      {project.totalCost > 0 && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <DollarSign className="h-3 w-3 text-green-600" />
          <span className="font-medium text-green-700">
            {formatCurrency(project.totalCost)}
          </span>
        </div>
      )}

      {project.totalDays > 0 && (
        <div className="kanban-card-meta mt-1">
          <Calendar className="h-3 w-3" />
          <span>{(project.totalDays / 100).toFixed(1)} days</span>
        </div>
      )}
    </div>
  );
}

// ============ Create Modal ============

function CreateOpportunityModal({ onClose }: { onClose: () => void }) {
  const createOpportunity = useCreateOpportunity();
  const { data: typesData } = useRateCardTypes();
  const navigate = useNavigate();
  const [customerError, setCustomerError] = useState<string | undefined>();

  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    title: '',
    description: '',
    servicesValue: '',
    currency: 'USD' as 'GBP' | 'USD' | 'EUR',
    fundingTypeId: '',
    contractStartDate: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate customer selection (customer-first architecture)
    if (!formData.customerId) {
      setCustomerError('Please select a customer. Customers must be created first.');
      return;
    }
    setCustomerError(undefined);

    try {
      const result = await createOpportunity.mutateAsync({
        customerId: formData.customerId,
        title: formData.title,
        description: formData.description || undefined,
        servicesValue: formData.servicesValue ? Math.round(parseFloat(formData.servicesValue) * 100) : undefined,
        currency: formData.currency,
        fundingTypeId: formData.fundingTypeId || undefined,
        contractStartDate: formData.contractStartDate || undefined,
      });
      onClose();
      navigate(`/opportunities/${result.opportunity.id}`);
    } catch (error) {
      console.error('Failed to create opportunity:', error);
    }
  };

  const handleCustomerChange = (customerId: string, customerName: string) => {
    setFormData({
      ...formData,
      customerId,
      customerName,
    });
    if (customerId) {
      setCustomerError(undefined);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <div className="dialog-header">
          <h2 className="dialog-title">New Opportunity</h2>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Customer *</label>
            <p className="text-xs text-gray-500 mb-2">
              Select an existing customer. Need a new one?{' '}
              <a href="/customers?create=true" className="text-primary-600 hover:underline">
                Create customer first
              </a>
            </p>
            <CustomerSelect
              value={formData.customerId || null}
              onChange={handleCustomerChange}
              required
              placeholder="Select a customer..."
              error={customerError}
            />
          </div>

          <div>
            <label className="label">Opportunity Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="e.g., VCF 9.0 Implementation"
              className="input mt-1 w-full"
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="input mt-1 w-full"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Services Value</label>
              <input
                type="number"
                value={formData.servicesValue}
                onChange={(e) =>
                  setFormData({ ...formData, servicesValue: e.target.value })
                }
                placeholder="0"
                className="input mt-1 w-full"
              />
            </div>
            <div>
              <label className="label">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    currency: e.target.value as 'GBP' | 'USD' | 'EUR',
                  })
                }
                className="input mt-1 w-full"
              >
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Funding</label>
              <select
                value={formData.fundingTypeId}
                onChange={(e) => setFormData({ ...formData, fundingTypeId: e.target.value })}
                className="input mt-1 w-full"
              >
                <option value="">TBC</option>
                {typesData?.types?.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Contract Start</label>
              <input
                type="date"
                value={formData.contractStartDate}
                onChange={(e) =>
                  setFormData({ ...formData, contractStartDate: e.target.value })
                }
                className="input mt-1 w-full"
              />
            </div>
          </div>
        </form>

        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.customerId || !formData.title || createOpportunity.isPending}
            className="btn-primary"
          >
            {createOpportunity.isPending ? 'Creating...' : 'Create Opportunity'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Helpers ============

function formatCurrency(cents: number, currency: 'GBP' | 'USD' | 'EUR' = 'USD'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
